import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Nav } from 'react-bootstrap';
import showToast from '../../../utils/toast';

const MobileRecharge = () => {
  const [formData, setFormData] = useState({
    mobileNumber: '',
    operator: '',
    circle: '',
    amount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [planFilters, setPlanFilters] = useState({
    search: ''
  });
  const [activeCategory, setActiveCategory] = useState('All');

  // Sample operators
  const operators = [
    'Airtel',
    'Jio',
    'Vi (Vodafone Idea)',
    'BSNL',
    'MTNL'
  ];

  // Sample circles
  const circles = [
    'Maharashtra & Goa',
    'Delhi & NCR',
    'Mumbai',
    'Kolkata',
    'Chennai',
    'Karnataka',
    'Andhra Pradesh & Telangana',
    'Tamil Nadu',
    'Kerala',
    'Punjab',
    'Haryana',
    'UP East',
    'UP West',
    'Rajasthan',
    'Gujarat',
    'MP & Chhattisgarh',
    'West Bengal',
    'Bihar & Jharkhand',
    'Assam',
    'North East',
    'Himachal Pradesh',
    'Jammu & Kashmir'
  ];

  // Sample recharge plans
  const rechargePlans = [
    {
      name: 'Daily Data Pack',
      price: '199',
      validity: '28 Days',
      data: '1.5 GB/day',
      benefits: 'Unlimited calls + 100 SMS/day',
      category: 'Data Pack'
    },
    {
      name: 'Unlimited Value Pack',
      price: '249',
      validity: '28 Days',
      data: '2 GB/day',
      benefits: 'Unlimited calls + 100 SMS/day + OTT trial',
      category: 'Unlimited'
    },
    {
      name: 'Long Validity Pack',
      price: '479',
      validity: '56 Days',
      data: '1.5 GB/day',
      benefits: 'Unlimited calls + 100 SMS/day + Weekend data rollover',
      category: 'Long Validity'
    },
    {
      name: 'Work From Home Pack',
      price: '599',
      validity: '84 Days',
      data: '2 GB/day',
      benefits: 'Unlimited calls + 100 SMS/day + Extra 20 GB bulk data',
      category: 'Work & Study'
    },
    {
      name: 'Annual Value Pack',
      price: '2999',
      validity: '365 Days',
      data: '2 GB/day',
      benefits: 'Unlimited calls + 100 SMS/day + Premium OTT subscription',
      category: 'Annual'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'amount') {
      setSelectedPlanName('');
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePlanSelect = (plan) => {
    setFormData(prev => ({
      ...prev,
      amount: plan.price
    }));
    setErrors(prev => ({
      ...prev,
      amount: ''
    }));
    setSelectedPlanName(plan.name);
  };

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setPlanFilters({
      search: value
    });
  };

  const resetPlanFilters = () => {
    setPlanFilters({
      search: ''
    });
    setActiveCategory('All');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.operator) {
      newErrors.operator = 'Please select an operator';
    }

    if (!formData.circle) {
      newErrors.circle = 'Please select a circle';
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) < 10) {
      newErrors.amount = 'Invalid Amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      showToast.success(`Recharge of ₹${formData.amount} initiated for ${formData.mobileNumber}`);
      setFormData({
        mobileNumber: '',
        operator: '',
        circle: '',
        amount: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const isMobileEntered = /^\d{10}$/.test(formData.mobileNumber);
  const planCategories = ['All', ...new Set(rechargePlans.map(plan => plan.category))];
  const filteredPlans = (() => {
    if (!isMobileEntered) {
      return [];
    }

    let plans = [...rechargePlans];

    const amountValue = parseInt(formData.amount, 10);
    if (formData.amount && !Number.isNaN(amountValue)) {
      plans = plans.filter(plan => parseInt(plan.price, 10) >= amountValue);
    }

    if (activeCategory !== 'All') {
      plans = plans.filter(plan => plan.category === activeCategory);
    }

    const query = planFilters.search.trim().toLowerCase();
    if (query) {
      const queryNumber = parseInt(query, 10);
      plans = plans.filter(plan => {
        const searchableText = `${plan.name} ${plan.validity} ${plan.data} ${plan.benefits}`.toLowerCase();
        const matchesText = searchableText.includes(query);
        const matchesPrice = !Number.isNaN(queryNumber) && parseInt(plan.price, 10) >= queryNumber;
        const matchesPriceExact = plan.price.includes(query);
        return matchesText || matchesPrice || matchesPriceExact;
      });
    }

    return plans;
  })();

  return (
    <Container fluid className="mobile-recharge-page p-4">
      {/* Page Header */}
      <marquee
        behavior="scroll"
        direction="left"
        scrollamount="6"
        style={{
          color: '#0f172a',
          fontWeight: 600,
          marginBottom: '0.75rem',
          background: '#ecfeff',
          padding: '0.35rem 0.75rem',
          borderRadius: '8px',
          border: '1px solid #bae6fd',
          fontSize: '13px'
        }}
      >
        Get exciting cashback offers on select mobile recharges. Recharge now and stay connected!
      </marquee>
      <div className="page-header mb-4" style={{ marginTop: 0 }}>
        <h2 className="mb-1" style={{ fontSize: '24px', fontWeight: 600, color: '#2c2268' }}>
          <i className="bi bi-phone me-2"></i>
          Mobile Recharge
        </h2>
        <p className="text-muted mb-0">Quick and easy mobile recharge service</p>
      </div>

      <Row>
        {/* Left Side - Recharge Form */}
        <Col lg={5} xl={4} className="mb-4">
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
              <Form onSubmit={handleSubmit} className="retailer-form">
                <Row>
                  {/* Mobile Number */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="mobileNumber">
                      <div className="input-with-icon">
                        <i className="bi bi-phone input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Control
                          type="text"
                          name="mobileNumber"
                          placeholder="Enter Mobile Number"
                          value={formData.mobileNumber}
                          onChange={handleInputChange}
                          maxLength={10}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.mobileNumber}
                        />
                      </div>
                      {errors.mobileNumber && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.mobileNumber}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Operator */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="operator">
                      <div className="input-with-icon">
                        <i className="bi bi-reception-4 input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Select
                          name="operator"
                          value={formData.operator}
                          onChange={handleInputChange}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.operator}
                        >
                          <option value="">Select Operator</option>
                          {operators.map((op, index) => (
                            <option key={index} value={op}>{op}</option>
                          ))}
                        </Form.Select>
                      </div>
                      {errors.operator && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.operator}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Circle */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="circle">
                      <div className="input-with-icon">
                        <i className="bi bi-globe input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Select
                          name="circle"
                          value={formData.circle}
                          onChange={handleInputChange}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.circle}
                        >
                          <option value="">Select Circle</option>
                          {circles.map((circle, index) => (
                            <option key={index} value={circle}>{circle}</option>
                          ))}
                        </Form.Select>
                      </div>
                      {errors.circle && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.circle}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Amount */}
                  <Col md={12} className="mb-3">
                    <Form.Group controlId="amount">
                      <div className="input-with-icon">
                        <i className="bi bi-currency-rupee input-icon" style={{ fontSize: '16px', color: '#0d6efd' }}></i>
                        <Form.Control
                          type="text"
                          name="amount"
                          placeholder="Enter Amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          className="ps-5 retailer-input"
                          isInvalid={!!errors.amount}
                        />
                      </div>
                      {errors.amount && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.amount}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Warning Message */}
                  <Col md={12} className="mb-2">
                    <div style={{
                      color: '#dc3545',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      *Recharge once done, cannot be reversed.
                    </div>
                  </Col>

                  {/* Submit Button */}
                  <Col md={12}>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-100"
                      style={{
                        backgroundColor: '#1e1850',
                        border: 'none',
                        padding: '12px 32px',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '15px',
                        boxShadow: '0 4px 6px rgba(30, 24, 80, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#0f0c30';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(30, 24, 80, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#1e1850';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(30, 24, 80, 0.3)';
                      }}
                    >
                      {isSubmitting ? 'Processing...' : 'Proceed'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Side - Recharge Plans */}
        <Col lg={7} xl={8} className="mb-4">
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom" style={{ padding: '16px 24px' }}>
              <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c2268' }}>
                <i className="bi bi-card-list me-2"></i>
                Recommended Recharge Plans
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {isMobileEntered && (
                <>
                  <div className="plan-filters border-bottom d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                    <Form.Control
                      type="text"
                      name="search"
                      value={planFilters.search}
                      onChange={handleFilterChange}
                      placeholder="Search plans by data, benefits, validity, or enter amount (e.g., 299)"
                      className="filter-control flex-grow-1 retailer-input"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={resetPlanFilters}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="plan-tabs border-bottom">
                    <Nav variant="tabs" activeKey={activeCategory}>
                      {planCategories.map(category => (
                        <Nav.Item key={category}>
                          <Nav.Link
                            eventKey={category}
                            onClick={() => setActiveCategory(category)}
                          >
                            {category}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </div>
                </>
              )}
              {!isMobileEntered ? (
                <div className="p-4 text-center text-muted" style={{ fontSize: '14px' }}>
                  Enter a valid 10-digit mobile number to view recommended plans.
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="p-4 text-center text-muted" style={{ fontSize: '14px' }}>
                  No plans match the entered amount. Try adjusting the recharge amount.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Plan
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Validity
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Data
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>
                          Benefits
                        </th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555', borderBottom: '2px solid #dee2e6', fontSize: '14px', textAlign: 'right' }}>
                          Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlans.map((plan, index) => {
                        const isSelected = selectedPlanName === plan.name;
                        return (
                          <tr
                            key={index}
                            onClick={() => handlePlanSelect(plan)}
                            style={{
                              transition: 'background-color 0.2s',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#e7f1ff' : 'transparent'
                            }}
                          >
                            <td style={{ padding: '12px 16px', color: '#333', fontSize: '13px', fontWeight: 600 }}>
                              {plan.name}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                              {plan.validity}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                              {plan.data}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#666', fontSize: '13px' }}>
                              {plan.benefits}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#1e1850', fontSize: '13px', fontWeight: 700, textAlign: 'right' }}>
                              ₹{plan.price}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Custom Styles */}
      <style jsx="true">{`
        .mobile-recharge-page {
          background-color: #fff;
          min-height: calc(100vh - 100px);
        }

        .input-with-icon {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          pointer-events: none;
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .plan-filters {
          padding: 16px 24px;
          background-color: #f9fafc;
        }

        .plan-tabs {
          padding: 0 24px;
          background-color: #ffffff;
        }

        .plan-tabs .nav-link {
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #495057;
          border: none;
          border-bottom: 2px solid transparent;
        }

        .plan-tabs .nav-link.active {
          color: #1e1850;
          border-bottom-color: #1e1850;
          background-color: rgba(30, 24, 80, 0.08);
          border-radius: 6px 6px 0 0;
        }

        .plan-tabs .nav-link:hover {
          color: #1e1850;
          background-color: rgba(30, 24, 80, 0.06);
        }

        .filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #495057;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .filter-control {
          font-size: 13px;
        }

        input:focus, select:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
        }

        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
          }
        }
      `}</style>
    </Container>
  );
};

export default MobileRecharge;

